import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faYoutube, faExpand } from "@fortawesome/free-solid-svg-icons";
import { faYoutube as faYoutubeBrand } from "@fortawesome/free-brands-svg-icons";

/**
 * YoutubePlayer – a polished modal YouTube IFrame player.
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - url: string  (full YouTube URL or playlist URL)
 *  - title: string
 */
const YoutubePlayer = ({ isOpen, onClose, url, title = "مشاهدة الفيديو" }) => {
  // Convert any YouTube URL to embeddable form
  const getEmbedUrl = (rawUrl) => {
    if (!rawUrl) return "";
    try {
      const u = new URL(rawUrl);
      // playlist
      if (u.searchParams.get("list")) {
        const listId = u.searchParams.get("list");
        const videoId = u.searchParams.get("v");
        return videoId
          ? `https://www.youtube.com/embed/${videoId}?list=${listId}&autoplay=1&rel=0`
          : `https://www.youtube.com/embed/videoseries?list=${listId}&autoplay=1&rel=0`;
      }
      // regular watch?v=
      const videoId = u.searchParams.get("v");
      if (videoId) return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
      // already embed or youtu.be
      if (u.hostname === "youtu.be") {
        return `https://www.youtube.com/embed${u.pathname}?autoplay=1&rel=0`;
      }
      if (u.pathname.startsWith("/embed/")) return rawUrl;
    } catch (_) { }
    return rawUrl;
  };

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const isIframeString = url?.trim().startsWith("<iframe");

  // Extract src from iframe string if applicable
  const getDisplayUrl = () => {
    if (isIframeString) {
      const match = url.match(/src=["']([^"']+)["']/);
      return match ? match[1] : "#";
    }
    return url;
  };

  const embedUrl = isIframeString ? "" : getEmbedUrl(url);
  const displayUrl = getDisplayUrl();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="yt-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            className="yt-modal"
            initial={{ scale: 0.88, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.88, opacity: 0, y: 40 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
          >
            {/* Header */}
            <div className="yt-modal-header">
              <div className="yt-modal-title">
                <div className="yt-logo-icon">
                  <FontAwesomeIcon icon={faYoutubeBrand} />
                </div>
                <span>{title}</span>
              </div>
              <button className="yt-close-btn" onClick={onClose} title="إغلاق">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* IFrame */}
            <div className="yt-player-wrapper">
              {isIframeString ? (
                <div 
                  className="yt-injected-iframe"
                  dangerouslySetInnerHTML={{ __html: url }}
                />
              ) : (
                <iframe
                  src={embedUrl}
                  title={title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="yt-iframe"
                  style={{ border: 0 }}
                />
              )}
            </div>

            {/* Footer hint */}
            <div className="yt-modal-footer">
              <span>اضغط <kbd>Esc</kbd> للإغلاق</span>
              <a
                href={displayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="yt-open-link"
              >
                <FontAwesomeIcon icon={faExpand} />
                فتح على يوتيوب
              </a>
            </div>
          </motion.div>

          <style jsx>{`
            .yt-overlay {
              position: fixed;
              inset: 0;
              background: rgba(0, 0, 50, 0.75);
              backdrop-filter: blur(8px);
              -webkit-backdrop-filter: blur(8px);
              z-index: 99999;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 16px;
                font-family: "Zain", sans-serif;
              direction: rtl;
            }

            .yt-modal {
              background: #0f0f1a;
              border-radius: 20px;
              width: 100%;
              max-width: 900px;
              overflow: hidden;
              box-shadow:
                0 0 0 1px rgba(255,255,255,0.08),
                0 30px 80px rgba(0,0,0,0.6),
                0 0 60px rgba(124,58,237,0.15);
              display: flex;
              flex-direction: column;
            }

            /* Header */
            .yt-modal-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 14px 20px;
              background: linear-gradient(135deg, #1a1a2e, #16213e);
              border-bottom: 1px solid rgba(255,255,255,0.07);
            }

            .yt-modal-title {
              display: flex;
              align-items: center;
              gap: 10px;
              color: #e2e8f0;
              font-weight: 700;
              font-size: 15px;
            }

            .yt-logo-icon {
              width: 32px;
              height: 32px;
              background: #ff0000;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #fff;
              font-size: 16px;
            }

            .yt-close-btn {
              width: 34px;
              height: 34px;
              background: rgba(255,255,255,0.08);
              border: 1px solid rgba(255,255,255,0.1);
              border-radius: 8px;
              color: #94a3b8;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 15px;
              transition: all 0.2s;
            }

            .yt-close-btn:hover {
              background: rgba(239,68,68,0.2);
              border-color: rgba(239,68,68,0.4);
              color: #ef4444;
            }

            /* Player */
            .yt-player-wrapper {
              position: relative;
              width: 100%;
              aspect-ratio: 16 / 9;
              background: #000;
            }

            .yt-iframe {
              position: absolute;
              inset: 0;
              width: 100%;
              height: 100%;
            }

            .yt-injected-iframe, 
            .yt-injected-iframe :global(iframe) {
              position: absolute;
              inset: 0;
              width: 100% !important;
              height: 100% !important;
              border: 0;
            }

            /* Footer */
            .yt-modal-footer {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 10px 20px;
              background: #0a0a14;
              font-size: 13px;
              color: #64748b;
            }

            .yt-modal-footer kbd {
              background: rgba(255,255,255,0.1);
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 12px;
              border: 1px solid rgba(255,255,255,0.15);
              color: #94a3b8;
              font-family: inherit;
            }

            .yt-open-link {
              display: flex;
              align-items: center;
              gap: 6px;
              color: #a78bfa;
              text-decoration: none;
              font-weight: 600;
              font-size: 13px;
              transition: color 0.2s;
            }

            .yt-open-link:hover {
              color: #7c3aed;
            }

            @media (max-width: 600px) {
              .yt-modal {
                border-radius: 14px;
                max-height: 92vh;
              }

              .yt-modal-title span {
                font-size: 13px;
                max-width: 180px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default YoutubePlayer;
